import * as THREE from 'three';

import { IBoundaryEdge } from '../models/abstract/BoundaryEdge';

const EPS = 1e-4;

const samePt = (a: THREE.Vector3, b: THREE.Vector3, eps = EPS) => Math.abs(a.x - b.x) <= eps && Math.abs(a.y - b.y) <= eps;

export type OrderedBoundaryEdge<T extends IBoundaryEdge = IBoundaryEdge> = {
  edge: T;
  s: THREE.Vector3;
  e: THREE.Vector3;
  dir: THREE.Vector3;
};

export type BoundaryIndicatorSegment<T extends IBoundaryEdge = IBoundaryEdge> = {
  edge: T;
  startPoint: THREE.Vector3;
  endPoint: THREE.Vector3;
};

function getRawEdgePointAt(edge: IBoundaryEdge, t: number): THREE.Vector3 {
  if ((edge.wall as any).pointAt) return (edge.wall as any).pointAt(t);

  const a = (edge.wall as any).startPoint as THREE.Vector3;
  const b = (edge.wall as any).endPoint as THREE.Vector3;

  return new THREE.Vector3(a.x + (b.x - a.x) * t, a.y + (b.y - a.y) * t, a.z + (b.z - a.z) * t);
}

export function orderBoundaryEdges<T extends IBoundaryEdge>(edges: T[]): OrderedBoundaryEdge<T>[] {
  const es = (edges || []).filter(Boolean);
  if (es.length === 0) return [];

  const getRawGeometry = (edge: T) => {
    const pt0 = getRawEdgePointAt(edge, edge.t0);
    const pt1 = getRawEdgePointAt(edge, edge.t1);

    return edge.reversed ? { s: pt1.clone(), e: pt0.clone() } : { s: pt0.clone(), e: pt1.clone() };
  };

  const used = new Array(es.length).fill(false);
  const out: OrderedBoundaryEdge<T>[] = [];
  const geom0 = getRawGeometry(es[0]);

  used[0] = true;
  out.push({ edge: es[0], s: geom0.s, e: geom0.e, dir: geom0.e.clone().sub(geom0.s).normalize() });

  let lastEnd = geom0.e.clone();
  let remaining = es.length - 1;
  let iterations = 0;

  while (remaining > 0 && iterations < es.length * 2) {
    iterations++;

    let found = false;

    for (let i = 0; i < es.length; i++) {
      if (used[i]) continue;

      const geom = getRawGeometry(es[i]);

      if (samePt(lastEnd, geom.s)) {
        out.push({ edge: es[i], s: geom.s, e: geom.e, dir: geom.e.clone().sub(geom.s).normalize() });
        lastEnd = geom.e.clone();
        used[i] = true;
        remaining--;
        found = true;
        break;
      }

      if (samePt(lastEnd, geom.e)) {
        out.push({ edge: es[i], s: geom.e, e: geom.s, dir: geom.s.clone().sub(geom.e).normalize() });
        lastEnd = geom.s.clone();
        used[i] = true;
        remaining--;
        found = true;
        break;
      }
    }

    if (!found) break;
  }

  return out;
}

export function calculateBoundaryPolygonOrientation<T extends IBoundaryEdge>(orderedEdges: OrderedBoundaryEdge<T>[]): number {
  if (!orderedEdges.length) return 0;

  const points = orderedEdges.map((ordered) => ordered.edge.startPoint || ordered.s).filter(Boolean);
  if (points.length < 3) return 0;

  let area = 0;

  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];

    area += a.x * b.y - b.x * a.y;
  }

  return Math.sign(area);
}

function lineIntersection2D(p1: THREE.Vector2, p2: THREE.Vector2, p3: THREE.Vector2, p4: THREE.Vector2): THREE.Vector2 {
  const x1 = p1.x;
  const y1 = p1.y;
  const x2 = p2.x;
  const y2 = p2.y;
  const x3 = p3.x;
  const y3 = p3.y;
  const x4 = p4.x;
  const y4 = p4.y;

  const den = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(den) < 1e-9) return new THREE.Vector2(x2, y2);

  const px = ((x1 * y2 - y1 * x2) * (x3 - x4) - (x1 - x2) * (x3 * y4 - y3 * x4)) / den;
  const py = ((x1 * y2 - y1 * x2) * (y3 - y4) - (y1 - y2) * (x3 * y4 - y3 * x4)) / den;

  return new THREE.Vector2(px, py);
}

export function calculateInsetFloorPolygonForOrderedEdges<T extends IBoundaryEdge>(
  orderedEdges: OrderedBoundaryEdge<T>[],
  polygonOrientation: number,
): THREE.Vector3[] | null {
  if (orderedEdges.length < 3) return null;

  const offsetLines: Array<{ start: THREE.Vector2; end: THREE.Vector2 }> = [];

  for (const seg of orderedEdges) {
    const wall = seg.edge.wall;
    const halfDepth = ((wall as any).depth ?? 0) / 2;
    const dir = seg.e.clone().sub(seg.s).normalize();

    const normal = polygonOrientation === 1 ? new THREE.Vector3(-dir.y, dir.x, 0) : new THREE.Vector3(dir.y, -dir.x, 0);

    const sFloor = seg.s.clone().add(normal.clone().multiplyScalar(halfDepth));
    const eFloor = seg.e.clone().add(normal.clone().multiplyScalar(halfDepth));

    offsetLines.push({ start: new THREE.Vector2(sFloor.x, sFloor.y), end: new THREE.Vector2(eFloor.x, eFloor.y) });
  }

  const verts: THREE.Vector3[] = [];
  const n = offsetLines.length;

  for (let i = 0; i < n; i++) {
    const curr = offsetLines[i];
    const next = offsetLines[(i + 1) % n];
    const inter = lineIntersection2D(curr.start, curr.end, next.start, next.end);

    if (!Number.isFinite(inter.x) || !Number.isFinite(inter.y) || Math.abs(inter.x) > 100000) return null;

    verts.push(new THREE.Vector3(inter.x, inter.y, 0));
  }

  return verts;
}

export function calculateBoundaryIndicatorSegments<T extends IBoundaryEdge>(
  orderedEdges: OrderedBoundaryEdge<T>[],
  polygonOrientation: number,
): BoundaryIndicatorSegment<T>[] {
  const insetPts = calculateInsetFloorPolygonForOrderedEdges(orderedEdges, polygonOrientation);
  if (!insetPts || insetPts.length !== orderedEdges.length) return [];

  return orderedEdges.map((seg, index) => ({
    edge: seg.edge,
    startPoint: insetPts[(index - 1 + insetPts.length) % insetPts.length].clone(),
    endPoint: insetPts[index].clone(),
  }));
}
